import type * as Sentry from '@sentry/browser'

// Returns why an error should not be reported, or null when it's ours.
export function isThirdPartyError(
  event: Sentry.ErrorEvent,
  origin: string,
  basePath: string
): string | null {
  const exception = event.exception?.values?.[0]
  const type = exception?.type || ''

  if (isDomEventInsteadOfError(type)) {
    return `non-Error event type: ${type}`
  }
  if (isBareValueRejection(type, exception?.value)) {
    return 'non-Error promise rejection'
  }

  const filenames = stackFilenames(event)
  if (filenames.length === 0) {
    // No stack, or only anonymous/eval frames: can't attribute it, keep it.
    return null
  }
  if (!filenames.some((filename) => isOurScript(filename, origin, basePath))) {
    return `no first-party frames (${type})`
  }
  return null
}

// Analytics and chat widgets dispatch DOM events that Sentry picks up as
// errors.
function isDomEventInsteadOfError(type: string) {
  return type === 'CustomEvent' || type === 'Event'
}

// Nothing of ours rejects with a bare value, and such rejections carry no
// stack to tell us otherwise (Outlook SafeLink's crawler is the usual source).
function isBareValueRejection(type: string, value: string | undefined) {
  return (
    type === 'UnhandledRejection' &&
    Boolean(value?.startsWith('Non-Error promise rejection captured'))
  )
}

function stackFilenames(event: Sentry.ErrorEvent): string[] {
  const frames =
    event.exception?.values?.flatMap(
      (value) => value.stacktrace?.frames || []
    ) || []
  return frames
    .map((frame) => frame.filename)
    .filter((filename): filename is string => Boolean(filename))
}

// Everything we ship runs from `${base}assets/`; the only inline scripts are
// the RudderStack loader and its two-line config (+bodyHtmlEnd.ts). A frame
// whose filename is the page URL itself is a script somebody else injected
// into the document: extension, proxy, crawler, in-app browser.
//
// Under `vike dev` modules are served from /@fs/ and /renderer/ instead, so
// every error is dropped there; the console.info in beforeSend shows it.
function isOurScript(filename: string, origin: string, basePath: string) {
  const base = basePath.endsWith('/') ? basePath : `${basePath}/`
  return filename.startsWith(`${origin}${base}assets/`)
}
